import React, { isValidElement, useCallback, useMemo } from 'react';

import SalonModal from 'components/dialog/SalonModal.component';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';
import isFn from 'utils/predicates/function/isFn.util';

const CTAG = 'WizardModal';
const L = logger(CTAG);

const { cloneElement } = React;
const { map, toArray } = React.Children;

// moniker interface, serves only for filtering
const State = () => null;

// React nodes can be null, undefined, true, false, string etc.
// But this predicate is interested only in components which are functions
// Those would be our custom developed ones, not strings for DOM or other values
const isCustomReactNode = (reactNode) => isValidElement(reactNode) && isFn(reactNode && reactNode.type);

const fromReactNodeToChildren = (reactNode) => toArray(reactNode.props.children);

const WizardModal = ({ state, children, onChange, ...modalProps }) => {
  L.debug('()', { state, children, onChange }, modalProps);

  // remove all nodes that aren't State as a direct child of the wizard modal
  const stateChildren = toArray(children).filter(({ type }) => type === State);

  // allowed states are only those that have been found as `value` prop of `State` child
  const allowedStates = stateChildren.map((child) => child.props.value);

  const handleNext = useCallback(
    (ev) => {
      const { value, ...extra } = ev ?? {};
      return void (allowedStates.includes(value)
        ? call(`${CTAG}.onChange`, onChange, { ...extra, value })
        : L.warn('handleNext()', 'Invalid next state:', value, 'not found in allowed states:', allowedStates));
    },
    [allowedStates, onChange],
  );

  const childProps = useMemo(
    // more elaborate state name to avoid clash with existing props
    () => ({ wizardModalState: state, onNext: handleNext }),
    [handleNext, state],
  );

  // NOTE: there are several levels to drill down and clone in order to add props to valid rendered components
  // WizardModal contains State which contains SalonModal.Body or similar node which contains what we need
  const addPropsToDescendants = (stateNode) =>
    // first must filter out all nodes that aren't under a State node with the proper state
    stateNode.props.value !== state
      ? null
      : map(fromReactNodeToChildren(stateNode), (salonModalNode) =>
          !isCustomReactNode(salonModalNode)
            ? // can't support children if it is a null or true/false or a simple string, so just return it
              salonModalNode
            : // must clone an element in order to change its children, they are part of the props
              cloneElement(
                salonModalNode, //
                // no new props for this node, components like SalonModal.Body don't support them
                null, //
                // but loop over the children of SalonModal.Body and they might support wizard's new props
                map(
                  fromReactNodeToChildren(salonModalNode),
                  // clone and add new props only if the node can support them, e.g. null or simple text can't
                  (anyNode) => (isCustomReactNode(anyNode) ? cloneElement(anyNode, childProps) : anyNode),
                ),
              ),
        );

  return <SalonModal {...modalProps}>{map(stateChildren, addPropsToDescendants)}</SalonModal>;
};

Object.assign(WizardModal, {
  State,
  // Renaming and re-exporting SalonModal helper components as a way of abstraction and improved DX
  Body: SalonModal.Body,
  Footer: SalonModal.Footer,
  Title: SalonModal.Title,
  Header: SalonModal.Header,
});

export default WizardModal;
